/**
 * 테마(다크/라이트/시스템) 저장과 적용. (00-architecture.md 1.2절)
 *
 * 🔴 쿠키가 아니라 localStorage 다
 *   쿠키를 쓸 이유는 서버가 그 값을 알아야 할 때뿐이다.
 *   우리는 SSR 이 없고 서버가 HTML 을 만들지 않으므로 서버는 테마를 알 필요가 없다.
 *   쿠키로 하면 매 요청 헤더에 실려 나가기만 하고 얻는 것이 없다.
 *   "단말기별로 남는다" 는 요구는 localStorage 로 그대로 만족된다.
 *
 * 🔴 첫 페인트 깜빡임은 index.html 의 인라인 스크립트가 막는다
 *   여기서 처리하면 React 가 마운트된 뒤라 이미 늦다.
 *   다크 사용자는 흰 화면이 한 번 번쩍인 뒤에 어두워진다.
 *   이 파일은 그 스크립트가 이미 정한 상태를 이어받아 바꾸는 역할만 한다.
 *   두 곳의 키 이름과 판정 규칙이 어긋나면 안 되므로 STORAGE_KEY 를 공유한다.
 */

export type ThemeMode = 'light' | 'dark' | 'system';

/** index.html 의 인라인 스크립트와 같은 키를 쓴다. 바꾸려면 양쪽을 함께 고친다. */
export const THEME_STORAGE_KEY = 'theme';

const MODES: ThemeMode[] = ['light', 'dark', 'system'];

/**
 * 저장된 설정을 읽는다. 없거나 이상한 값이면 system 이다.
 *
 * 기본값을 system 으로 두는 이유
 *   폰에서 야간 모드를 켜 둔 사람은 이 앱도 어둡기를 기대한다.
 *   light 를 기본으로 하면 매번 직접 바꿔야 한다.
 */
export function readThemeMode(): ThemeMode {
  try {
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    return MODES.includes(saved as ThemeMode) ? (saved as ThemeMode) : 'system';
  } catch {
    // 사파리 프라이빗 모드 등에서 localStorage 접근이 막힐 수 있다.
    // 테마 때문에 앱이 죽으면 안 된다.
    return 'system';
  }
}

/** OS 가 어두운 모드인지. system 모드일 때만 의미가 있다. */
export function prefersDark(): boolean {
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/** 지금 실제로 어둡게 그려야 하는가. */
export function isDark(mode: ThemeMode): boolean {
  return mode === 'dark' || (mode === 'system' && prefersDark());
}

/**
 * html 요소에 .dark 를 붙이거나 뗀다.
 *
 * CSS 는 .dark 하나만 안다. system 모드라는 개념은 여기서 끝나고
 * CSS 로 넘어가지 않는다. (index.css 주석)
 */
export function applyTheme(mode: ThemeMode): void {
  document.documentElement.classList.toggle('dark', isDark(mode));
}

/** 설정을 저장하고 즉시 적용한다. */
export function saveThemeMode(mode: ThemeMode): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, mode);
  } catch {
    // 저장에 실패해도 이번 세션 동안은 적용되게 한다.
  }
  applyTheme(mode);
}

/**
 * 순환 순서. 버튼 하나로 세 상태를 오간다.
 *
 * 설정 페이지를 만들지 않은 이유는 항목이 하나뿐이기 때문이다.
 */
export function nextThemeMode(mode: ThemeMode): ThemeMode {
  const index = MODES.indexOf(mode);
  return MODES[(index + 1) % MODES.length] ?? 'system';
}

/**
 * OS 설정이 바뀌는 것을 구독한다. system 모드에서만 반응한다.
 *
 * 이것이 없으면 폰이 밤에 자동으로 야간 모드로 바뀌어도
 * 열어둔 탭은 밝은 채로 남는다.
 *
 * @returns 구독 해제 함수
 */
export function watchSystemTheme(onChange: () => void): () => void {
  const query = window.matchMedia('(prefers-color-scheme: dark)');
  query.addEventListener('change', onChange);
  return () => query.removeEventListener('change', onChange);
}
